// crm.api.ts — version complète (audit + features manquantes)
import api from '@/lib/axios'

export interface CrmContact {
  id: string; firstName: string; lastName: string; email?: string
  phone?: string; mobile?: string; jobTitle?: string; companyId?: string
  company?: { id: string; name: string }
  owner?: { id: string; firstName: string; lastName: string }
  source?: string; tags: string[]; isActive: boolean; createdAt: string
  _count?: { opportunities: number; activities: number }
}

export interface CrmCompany {
  id: string; name: string; industry?: string; size?: string
  website?: string; phone?: string; email?: string
  address?: string; city?: string; country?: string
  ninea?: string; rccm?: string; notes?: string
  tags: string[]; isActive: boolean; createdAt: string
  _count?: { contacts: number; opportunities: number }
}

export interface PipelineStage {
  id: string; name: string; order: number; probability: number
  color?: string; isJournalRequired: boolean; isWon: boolean; isLost: boolean
  isActive: boolean; _count?: { opportunities: number }
}

export interface CrmOpportunity {
  id: string; title: string; stageId: string
  stage: PipelineStage
  contactId?: string; contact?: { id: string; firstName: string; lastName: string; email?: string }
  companyId?: string; company?: { id: string; name: string }
  assignedToId?: string
  assignedTo?: { id: string; user: { firstName: string; lastName: string; avatarUrl?: string } }
  amount?: number; currency: string; probability: number
  score: number; scoreLevel: 'COLD' | 'WARM' | 'HOT'
  source?: string
  expectedCloseDate?: string; actualCloseDate?: string
  wonLostReason?: string; description?: string; tags: string[]
  isActive: boolean; lastActivityAt?: string; journalUpdatedAt?: string
  createdAt: string; updatedAt: string
  _count?: { activities: number }
  // Détails chargés par getOpportunity
  activities?: any[]
  stageHistory?: any[]
  commissions?: Commission[]
}

export interface PipelineColumn {
  stage: PipelineStage; opportunities: CrmOpportunity[]; totalValue: number
}

export interface CrmActivity {
  id: string; type: string; subject: string; description: string
  nextAction: string; nextActionDate: string; interestLevel: string
  durationMinutes?: number; opportunityId?: string; contactId?: string
  opportunity?: { id: string; title: string }
  contact?: { id: string; firstName: string; lastName: string }
  performedById: string
  performedBy?: { id: string; firstName: string; lastName: string; avatarUrl?: string }
  performedAt: string; createdAt: string; attachmentUrl?: string
  // Rectifications tracées (B56), de la plus ancienne à la plus récente
  corrections?: CrmActivityCorrection[]
}

// Rectification ajoutée par l'auteur sous une activité (l'activité d'origine reste inchangée)
export interface CrmActivityCorrection {
  id: string; content: string; createdAt: string
  author: { id: string; firstName: string; lastName: string }
}

export interface SalesTarget {
  id: string; employeeId: string
  type: 'MONTHLY' | 'WEEKLY' | 'QUARTERLY' | 'ANNUAL'
  year: number; period: number
  targetType: 'REVENUE' | 'DEALS' | 'ACTIVITIES' | 'CALLS' | 'MEETINGS'
  targetValue: number; actualValue: number; currency: string; createdAt: string
  employee?: { id: string; user: { firstName: string; lastName: string; avatarUrl?: string } }
  progressRate?: number; periodProgress?: number
  isBehind?: boolean; isMidPeriod?: boolean
  status?: 'ACHIEVED' | 'ON_TRACK' | 'AT_RISK' | 'BELOW'
}

export interface SequenceStep {
  id: string; order: number; delayDays: number
  subject: string; bodyTemplate: string; isActive: boolean
}

export interface EmailSequence {
  id: string; name: string; description?: string
  triggerDays: number[]; isActive: boolean; createdAt: string
  steps: SequenceStep[]
  _count?: { opportunitySequences: number }
}

export interface OpportunitySequence {
  id: string; opportunityId: string; sequenceId: string
  sequence?: EmailSequence
  status: 'ACTIVE' | 'PAUSED' | 'COMPLETED' | 'STOPPED'
  currentStep: number; startedAt: string; completedAt?: string; stoppedAt?: string
}

export interface ContactSequence {
  id: string; contactId: string; sequenceId: string
  sequence?: EmailSequence
  status: 'ACTIVE' | 'PAUSED' | 'COMPLETED' | 'STOPPED'
  currentStep: number; startedAt: string; completedAt?: string
  stoppedAt?: string; stoppedReason?: string
}

export interface Commission {
  id: string; employeeId: string; opportunityId?: string
  amount: number; currency: string; status: string
  calculationDetail: Record<string, any>
  managerApprovedAt?: string; hrApprovedAt?: string; financeApprovedAt?: string
  contestedAt?: string; contestReason?: string; paidAt?: string
  payslipId?: string
  createdAt: string; updatedAt: string
  employee?: { user: { firstName: string; lastName: string } }
  opportunity?: { id: string; title: string; amount: number }
}

export interface CommissionPlan {
  id: string; name: string; description?: string
  type: 'FIXED_RATE' | 'TIERED' | 'BONUS_ON_TARGET'
  triggerEvent: string; isActive: boolean
  tiers: { id: string; fromAmount: number; toAmount?: number; rate: number; bonusAmount?: number }[]
  _count?: { assignments: number }
}

export interface LeaderboardEntry {
  rank: number; employee: any
  dealsWon: number; totalRevenue: number; activities: number; commission: number
}

const R = (r: any) => r.data.data

export const crmApi = {
  // Dashboard
  getDashboard:   (role?: string)    => api.get('/crm/dashboard', { params: { role } }).then(R),
  getForecast:    ()                 => api.get('/crm/forecast').then(R),

  // Étapes pipeline
  getStages:      ()                 => api.get('/crm/stages').then(R),
  createStage:    (data: any)        => api.post('/crm/stages', data).then(R),
  updateStage:    (id: string, data: any) => api.patch(`/crm/stages/${id}`, data).then(R),
  deleteStage:    (id: string)       => api.delete(`/crm/stages/${id}`).then(R),
  reorderStages:  (stageIds: string[]) => api.patch('/crm/stages/reorder', { stageIds }).then(R),

  // Pipeline board
  getPipeline:    (assignedToId?: string) => api.get('/crm/pipeline', { params: { assignedToId } }).then(R),

  // Contacts
  listContacts:   (p?: any)          => api.get('/crm/contacts', { params: p }).then(R),
  getContact:     (id: string)       => api.get(`/crm/contacts/${id}`).then(R),
  createContact:  (data: any)        => api.post('/crm/contacts', data).then(R),
  updateContact:  (id: string, data: any) => api.patch(`/crm/contacts/${id}`, data).then(R),
  deleteContact:  (id: string)       => api.delete(`/crm/contacts/${id}`).then(R),
  startContactSequence: (id: string, data: { sequenceId: string; requiresApproval?: boolean }) =>
    api.post(`/crm/contacts/${id}/sequences/start`, data).then(R),
  stopContactSequence: (id: string, reason?: string) =>
    api.post(`/crm/contacts/${id}/sequences/stop`, { reason }).then(R),

  // Entreprises
  listCompanies:  (p?: any)          => api.get('/crm/companies', { params: p }).then(R),
  getCompany:     (id: string)       => api.get(`/crm/companies/${id}`).then(R),
  createCompany:  (data: any)        => api.post('/crm/companies', data).then(R),
  updateCompany:  (id: string, d: any) => api.patch(`/crm/companies/${id}`, d).then(R),
  deleteCompany:  (id: string)       => api.delete(`/crm/companies/${id}`).then(R),

  // Opportunités
  listOpportunities:  (p?: any)      => api.get('/crm/opportunities', { params: p }).then(R),
  getOpportunity:     (id: string)   => api.get(`/crm/opportunities/${id}`).then(R),
  createOpportunity:  (data: any)    => api.post('/crm/opportunities', data).then(R),
  updateOpportunity:  (id: string, d: any) => api.patch(`/crm/opportunities/${id}`, d).then(R),
  deleteOpportunity:  (id: string)   => api.delete(`/crm/opportunities/${id}`).then(R),
  moveStage:          (id: string, d: any) => api.patch(`/crm/opportunities/${id}/move-stage`, d).then(R),
  updateScore:        (id: string, d: any) => api.patch(`/crm/opportunities/${id}/score`, d).then(R),
  startSequenceOnOpp: (opportunityId: string, data: { sequenceId: string; requiresApproval?: boolean }) =>
    api.post(`/crm/opportunities/${opportunityId}/sequences/start`, data).then(R),
  stopSequenceOnOpp: (opportunityId: string, reason?: string) =>
    api.post(`/crm/opportunities/${opportunityId}/sequences/stop`, { reason }).then(R),
  approveSequenceStep: (data: { oppSeqId: string; stepId: string }) =>
    api.post('/crm/sequences/approve-step', data).then(R),

  // Activités
  listActivities: (p?: any)          => api.get('/crm/activities', { params: p }).then(R),
  createActivity: (data: any)        => api.post('/crm/activities', data).then(R),
  correctActivity: (id: string, content: string): Promise<CrmActivityCorrection> =>
    api.post(`/crm/activities/${id}/corrections`, { content }).then(R),

  // Objectifs
  listTargets:        (p?: any)      => api.get('/crm/targets', { params: p }).then(R),
  createTarget:       (data: any)    => api.post('/crm/targets', data).then(R),
  updateTargetActual: (id: string, actualValue: number) =>
    api.patch(`/crm/targets/${id}/actual`, { actualValue }).then(R),
  deleteTarget:       (id: string)   => api.delete(`/crm/targets/${id}`).then(R),

  // Commissions
  listCommissions:     (p?: any)     => api.get('/crm/commissions', { params: p }).then(R),
  validateCommission:  (id: string, d: any) => api.patch(`/crm/commissions/${id}/validate`, d).then(R),
  contestCommission:   (id: string, reason: string) =>
    api.patch(`/crm/commissions/${id}/contest`, { reason }).then(R),
  reopenCommission:    (id: string)  => api.patch(`/crm/commissions/${id}/reopen`).then(R),
  integrateCommission: (id: string, payslipId: string) =>
    api.patch(`/crm/commissions/${id}/integrate`, { payslipId }).then(R),
  markCommissionPaid:  (id: string)  => api.patch(`/crm/commissions/${id}/mark-paid`).then(R),
  simulateCommission:  (employeeId: string, amount: number) =>
    api.get('/crm/commissions/simulate', { params: { employeeId, amount } }).then(R),
  getPayslipsForEmployee: (employeeId: string) =>
    api.get(`/crm/commissions/${employeeId}/payslips`).then(R),
  listCommissionPlans:  ()           => api.get('/crm/commission-plans').then(R),
  createCommissionPlan: (data: any)  => api.post('/crm/commission-plans', data).then(R),
  assignCommissionPlan: (planId: string, employeeId: string) =>
    api.post(`/crm/commission-plans/${planId}/assign/${employeeId}`).then(R),

  // Leaderboard
  getLeaderboard:     (period?: string) => api.get('/crm/leaderboard', { params: { period } }).then(R),

  // Taux de conversion
  getConversionRates: (employeeId?: string) =>
    api.get('/crm/conversion-rates', { params: { employeeId } }).then(R),

  // Analytics
  getAnalytics:         (p?: any)    => api.get('/crm/analytics', { params: p }).then(R),
  getFunnel:            (year?: number) => api.get('/crm/analytics/funnel', { params: { year } }).then(R),
  getAnalyticsForecast: (year?: number) => api.get('/crm/analytics/forecast', { params: { year } }).then(R),
  getTopPerformers:     (year?: number) => api.get('/crm/analytics/top-performers', { params: { year } }).then(R),
  getStagnant:          (days?: number) => api.get('/crm/analytics/stagnant', { params: { days } }).then(R),

  // Séquences drip — CRUD
  listSequences:   (p?: any)         => api.get('/crm/sequences', { params: p }).then(R),
  getSequence:     (id: string)      => api.get(`/crm/sequences/${id}`).then(R),
  createSequence:  (data: any)       => api.post('/crm/sequences', data).then(R),
  updateSequence:  (id: string, d: any) => api.patch(`/crm/sequences/${id}`, d).then(R),
  deleteSequence:  (id: string)      => api.delete(`/crm/sequences/${id}`).then(R),
}
